import { useCallback } from 'react';
import { ProCard } from '@ant-design/pro-components';
import { Form, Image, Input, Select, Space, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import Permission from '@/components/permission';
import { resolveAssetUrl } from '@/pages/drama-review/utils';
import AuthorNameIdCell from '@/pages/ugc-reports/components/AuthorNameIdCell';
import UgcReportListPage from '@/pages/ugc-reports/components/UgcReportListPage';
import { COMMENT_REPORT_TYPE_OPTIONS, REPORT_PERMISSION } from '@/pages/ugc-reports/constants';
import { buildTrailingReportColumns } from '@/pages/ugc-reports/hooks/useTrailingReportColumns';
import {
    formatDisplayId,
    getBelongingDramaPresentation,
    getCommentContent,
    getCommentUserId,
    getCommentUserNickname,
} from '@/pages/ugc-reports/utils';

import type { ProColumns } from '@ant-design/pro-components';
import type { UgcReportTicket } from '@/services/ugc-reports';

const numericInputProps = {
    getValueFromEvent: (e: { target?: { value?: string } }) =>
        String(e?.target?.value ?? '').replace(/\D/g, ''),
};

const PageUgcCommentReports: React.FC = () => {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const reportTypeOptions = COMMENT_REPORT_TYPE_OPTIONS.map((item) => ({
        label: formatMessage({ id: `ugcReports.contentType.${item.code}` }),
        value: item.code,
    }));

    const buildColumns = useCallback(
        ({
            expandedRowKeys,
            toggleExpand,
        }: {
            expandedRowKeys: string[];
            toggleExpand: (record: UgcReportTicket) => void;
        }) => {
            const trailing = buildTrailingReportColumns({
                dictionary: 'content',
                expandedRowKeys,
                toggleExpand,
                navigate,
            });
            return [
                {
                    title: <FormattedMessage id="ugcReports.columns.commentId" />,
                    width: 200,
                    render: (_: unknown, record: UgcReportTicket) => {
                        const commentId = record.comment_id || record.target_id;
                        return (
                            <Typography.Text copyable={{ text: commentId }}>
                                {formatDisplayId(commentId)}
                            </Typography.Text>
                        );
                    },
                },
                {
                    title: <FormattedMessage id="ugcReports.columns.commentContent" />,
                    width: 280,
                    render: (_: unknown, record: UgcReportTicket) => {
                        const content = getCommentContent(record);
                        if (!content) return '-';
                        return (
                            <Typography.Paragraph
                                copyable={{ text: content }}
                                ellipsis={{ rows: 3 }}
                                style={{ marginBottom: 0, wordBreak: 'break-word' }}
                            >
                                &ldquo;{content}&rdquo;
                            </Typography.Paragraph>
                        );
                    },
                },
                {
                    title: <FormattedMessage id="ugcReports.columns.commentAuthor" />,
                    width: 180,
                    render: (_: unknown, record: UgcReportTicket) => (
                        <AuthorNameIdCell
                            name={getCommentUserNickname(record)}
                            userId={getCommentUserId(record)}
                        />
                    ),
                },
                {
                    title: <FormattedMessage id="ugcReports.columns.belongingWork" />,
                    width: 340,
                    render: (_: unknown, record: UgcReportTicket) => {
                        const { cover, dramaId, title } = getBelongingDramaPresentation(record);
                        return (
                            <Space align="start" size={12}>
                                {cover ? (
                                    <Image
                                        src={resolveAssetUrl(cover)}
                                        width={48}
                                        height={64}
                                        style={{
                                            objectFit: 'cover',
                                            borderRadius: 4,
                                            flexShrink: 0,
                                        }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            width: 48,
                                            height: 64,
                                            borderRadius: 4,
                                            background: 'rgba(0,0,0,0.06)',
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                <Space direction="vertical" size={2} style={{ minWidth: 0 }}>
                                    <Typography.Text>
                                        <FormattedMessage id="ugcReports.columns.relatedType" />：
                                        <FormattedMessage id="ugcReports.columns.relatedDrama" />
                                    </Typography.Text>
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        <FormattedMessage id="ugcReports.columns.relatedDramaId" />
                                        ：
                                        {dramaId ? (
                                            <Typography.Text
                                                copyable={{ text: dramaId }}
                                                style={{ fontSize: 12 }}
                                            >
                                                {dramaId}
                                            </Typography.Text>
                                        ) : (
                                            '-'
                                        )}
                                    </Typography.Text>
                                    <Typography.Text strong style={{ wordBreak: 'break-word' }}>
                                        {title ? `《${title}》` : '-'}
                                    </Typography.Text>
                                </Space>
                            </Space>
                        );
                    },
                },
                trailing.reasonColumn,
                trailing.timeRangeColumn,
                trailing.reportCountColumn,
                trailing.actionColumn,
            ] as ProColumns<UgcReportTicket>[];
        },
        [navigate],
    );

    const searchFields = (
        <>
            <Form.Item name="report_type_code" style={{ marginBottom: 0 }}>
                <Select
                    allowClear
                    options={reportTypeOptions}
                    placeholder={formatMessage({ id: 'ugcReports.search.reportType' })}
                    style={{ width: 180 }}
                />
            </Form.Item>
            <Form.Item name="comment_id" style={{ marginBottom: 0 }} {...numericInputProps}>
                <Input
                    allowClear
                    inputMode="numeric"
                    placeholder={formatMessage({ id: 'ugcReports.search.commentId' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="comment_user_id" style={{ marginBottom: 0 }} {...numericInputProps}>
                <Input
                    allowClear
                    inputMode="numeric"
                    placeholder={formatMessage({ id: 'ugcReports.search.commentUserId' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="comment_user_nickname" style={{ marginBottom: 0 }}>
                <Input
                    allowClear
                    placeholder={formatMessage({
                        id: 'ugcReports.search.commentUserNickname',
                    })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="belonging_work_id" style={{ marginBottom: 0 }} {...numericInputProps}>
                <Input
                    allowClear
                    inputMode="numeric"
                    placeholder={formatMessage({ id: 'ugcReports.search.belongingWorkId' })}
                    style={{ width: 200 }}
                />
            </Form.Item>
            <Form.Item name="belonging_work_name" style={{ marginBottom: 0 }}>
                <Input
                    allowClear
                    placeholder={formatMessage({
                        id: 'ugcReports.search.belongingWorkName',
                    })}
                    style={{ width: 200 }}
                />
            </Form.Item>
        </>
    );

    return (
        <Permission permission={REPORT_PERMISSION}>
            <ProCard>
                <UgcReportListPage
                    targetType="COMMENT"
                    buildColumns={buildColumns}
                    searchFields={searchFields}
                    dictionary="content"
                    scrollX={1560}
                    withTimeFilter={false}
                />
            </ProCard>
        </Permission>
    );
};

export default PageUgcCommentReports;
